import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import * as ref from '@/api/reference';
import { useOptimisticMutation, removeById, replaceById } from './optimistic';
import { tempId } from '@/lib/utils';
import type { Activity, BillableParty, Client, CreditFeeTemplate } from '@/types';

/**
 * Reference data (clients, activities, billable parties) is small, changes
 * rarely and is needed by almost every screen, so it is cached for a long time
 * and shared. v1 refetched it per screen and never invalidated it, which is why
 * a newly created client wouldn't show up in the timesheet dropdown.
 */
const REFERENCE_STALE = 5 * 60_000;

export function useClients() {
  return useQuery({
    queryKey: qk.clients,
    queryFn: ({ signal }) => ref.fetchClients(signal),
    staleTime: REFERENCE_STALE
  });
}

export function useActivities() {
  return useQuery({
    queryKey: qk.activities,
    queryFn: ({ signal }) => ref.fetchActivities(signal),
    staleTime: REFERENCE_STALE
  });
}

export function useBillables() {
  return useQuery({
    queryKey: qk.billables,
    queryFn: ({ signal }) => ref.fetchBillables(signal),
    staleTime: REFERENCE_STALE
  });
}

export function useTeamUsers() {
  return useQuery({
    queryKey: qk.teamUsers,
    queryFn: ({ signal }) => ref.fetchTeamUsers(signal),
    staleTime: REFERENCE_STALE
  });
}

/** id -> name lookups, memo-free because the arrays are tiny and stable. */
export function useNameLookup() {
  const clients = useClients().data ?? [];
  const activities = useActivities().data ?? [];
  return {
    clientName: (id: string) => clients.find((c) => c.id === id)?.name ?? 'Unknown client',
    activityName: (id: string) => activities.find((a) => a.id === id)?.name ?? 'Unknown activity',
    client: (id: string) => clients.find((c) => c.id === id) ?? null
  };
}

// ------------------------------------------------------- activity sets

/** Suggested starter activities per profession (GET /api/activities/sets). */
export function useActivitySets() {
  return useQuery({
    queryKey: [...qk.activities, 'sets'],
    queryFn: ({ signal }) => ref.fetchActivitySets(signal),
    staleTime: REFERENCE_STALE
  });
}

export function useBulkAddActivities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (names: string[]) => ref.bulkAddActivities(names),
    onSuccess(result) {
      void qc.invalidateQueries({ queryKey: qk.activities });
      void qc.invalidateQueries({ queryKey: qk.setup });
      const n = result.created.length;
      toast.success(n > 0 ? `Added ${n} ${n === 1 ? 'activity' : 'activities'}` : 'Nothing new to add');
    },
    onError(error: unknown) {
      toast.error('Could not add those activities.', {
        description: error instanceof Error ? error.message : undefined
      });
    }
  });
}

// ------------------------------------------------- inline create (combobox)

/**
 * Creating a client or activity from a combobox is optimistic: the new name
 * appears in the list and is selected immediately. If the server rejects it
 * (e.g. duplicate activity name) the row is rolled back and the error surfaces.
 */
export function useCreateClient() {
  return useOptimisticMutation<Client[], { name: string }, Client>({
    mutationFn: ({ name }) => ref.createClient({ name }),
    errorMessage: 'Could not create that client.',
    optimistic: () => ({
      patches: [
        {
          key: qk.clients,
          exact: true,
          update: (list: never, vars) =>
            [
              {
                id: tempId('cli'),
                name: vars.name,
                status: 'active' as const,
                billable: null,
                hourlyRate: null
              },
              ...(list as Client[])
            ] as never
        }
      ],
      invalidate: [qk.clients, qk.setup]
    }),
    successMessage: (client) => `Added “${client.name}”`
  });
}

export function useCreateActivity() {
  return useOptimisticMutation<Activity[], { name: string }, Activity>({
    mutationFn: ({ name }) => ref.createActivity(name),
    errorMessage: 'Could not create that activity.',
    optimistic: () => ({
      patches: [
        {
          key: qk.activities,
          exact: true,
          update: (list: never, vars) =>
            [{ id: tempId('act'), name: vars.name }, ...(list as Activity[])] as never
        }
      ],
      invalidate: [qk.activities, qk.setup]
    }),
    successMessage: (activity) => `Added “${activity.name}”`
  });
}

// ------------------------------------------------------- manage-entity CRUD

type EntityKind = 'client' | 'activity' | 'billable';

const ENTITY_KEY = {
  client: qk.clients,
  activity: qk.activities,
  billable: qk.billables
} as const;

export function useUpdateEntity(kind: EntityKind) {
  return useOptimisticMutation<
    (Client | Activity | BillableParty)[],
    { id: string; patch: Record<string, unknown> },
    unknown
  >({
    mutationFn: ({ id, patch }) => {
      if (kind === 'client') return ref.updateClient(id, patch);
      if (kind === 'activity') return ref.updateActivity(id, String(patch.name ?? ''));
      return ref.updateBillable(id, patch);
    },
    errorMessage: 'Could not save that change.',
    optimistic: () => ({
      patches: [
        {
          key: ENTITY_KEY[kind],
          update: (list: never, vars) =>
            replaceById(list as { id: string }[], vars.id, vars.patch) as never
        }
      ],
      invalidate: [ENTITY_KEY[kind]]
    }),
    successMessage: 'Saved'
  });
}

export function useDeleteEntity(kind: EntityKind) {
  return useOptimisticMutation<{ id: string }[], { id: string }, unknown>({
    mutationFn: ({ id }) => {
      if (kind === 'client') return ref.deleteClient(id);
      if (kind === 'activity') return ref.deleteActivity(id);
      return ref.deleteBillable(id);
    },
    errorMessage: 'Could not delete that.',
    optimistic: () => ({
      patches: [
        {
          key: ENTITY_KEY[kind],
          update: (list: never, vars) => removeById(list as { id: string }[], vars.id) as never
        }
      ],
      invalidate: [ENTITY_KEY[kind]]
    }),
    successMessage: 'Deleted'
  });
}

export function useCreateEntity(kind: EntityKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => {
      if (kind === 'client')
        return ref.createClient({
          name: String(input.name),
          hourlyRate: (input.hourlyRate as number | null | undefined) ?? null
        });
      if (kind === 'activity') return ref.createActivity(String(input.name));
      return ref.createBillable(input as unknown as ref.BillableInput);
    },
    onSuccess() {
      void qc.invalidateQueries({ queryKey: ENTITY_KEY[kind] });
      void qc.invalidateQueries({ queryKey: qk.setup });
      toast.success('Created');
    },
    onError(error: unknown) {
      toast.error('Could not create that.', {
        description: error instanceof Error ? error.message : undefined
      });
    }
  });
}

export function useMergeEntities(kind: EntityKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; targetId: string | null; newName: string | null }) => {
      if (kind === 'client')
        return ref.mergeClients({
          clientIds: input.ids,
          targetClientId: input.targetId,
          newName: input.newName
        });
      if (kind === 'activity')
        return ref.mergeActivities({
          activityIds: input.ids,
          targetActivityId: input.targetId,
          newName: input.newName
        });
      return ref.mergeBillables({
        billableIds: input.ids,
        targetBillableId: input.targetId,
        newName: input.newName
      });
    },
    onSuccess() {
      void qc.invalidateQueries({ queryKey: ENTITY_KEY[kind] });
      // Merging rewrites entry and invoice associations too.
      void qc.invalidateQueries({ queryKey: qk.daycardsRoot });
      void qc.invalidateQueries({ queryKey: qk.invoicesRoot });
      toast.success('Merged');
    },
    onError() {
      toast.error('Could not merge those records.');
    }
  });
}

export function useSetClientBillable() {
  return useOptimisticMutation<Client[], { clientId: string; billableId: string | null }, unknown>({
    mutationFn: ({ clientId, billableId }) => ref.setClientBillable(clientId, billableId),
    errorMessage: 'Could not update the billing party.',
    optimistic: (_vars, qc) => {
      const billables = qc.getQueryData<BillableParty[]>(qk.billables) ?? [];
      return {
        patches: [
          {
            key: qk.clients,
            exact: true,
            update: (list: never, vars) => {
              const b = billables.find((x) => x.id === vars.billableId);
              return (list as Client[]).map((c) =>
                c.id === vars.clientId
                  ? {
                      ...c,
                      billable: b
                        ? {
                            id: b.id,
                            name: b.name,
                            address: b.address,
                            phone: b.phone,
                            emails: b.emails
                          }
                        : null
                    }
                  : c
              ) as never;
            }
          }
        ],
        invalidate: [qk.clients]
      };
    }
  });
}

// -------------------------------------------------- credit / fee templates

export function useCreditFeeTemplates(search?: string) {
  return useQuery({
    queryKey: [...qk.creditFeeTemplates, search ?? ''],
    queryFn: ({ signal }) => ref.fetchCreditFeeTemplates({ search }, signal),
    staleTime: REFERENCE_STALE
  });
}

export function useSaveCreditFeeTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreditFeeTemplate | Omit<CreditFeeTemplate, 'id'>) =>
      'id' in input && input.id
        ? ref.updateCreditFeeTemplate(input.id, input)
        : ref.createCreditFeeTemplate(input),
    onSuccess() {
      void qc.invalidateQueries({ queryKey: qk.creditFeeTemplates });
      toast.success('Saved');
    },
    onError() {
      toast.error('Could not save that template.');
    }
  });
}

export function useDeleteCreditFeeTemplate() {
  return useOptimisticMutation<CreditFeeTemplate[], { id: string }, unknown>({
    mutationFn: ({ id }) => ref.deleteCreditFeeTemplate(id),
    errorMessage: 'Could not delete that template.',
    optimistic: () => ({
      patches: [
        {
          key: qk.creditFeeTemplates,
          update: (list: never, vars) =>
            removeById(list as CreditFeeTemplate[], vars.id) as never
        }
      ],
      invalidate: [qk.creditFeeTemplates]
    }),
    successMessage: 'Template deleted'
  });
}
